import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import {
    AlertTriangle,
    ArrowDownLeft,
    Clock,
    Landmark,
    Loader2,
} from 'lucide-react'

import type { BalanceEvent, ReconcileStatus } from '@/lib/api/balance-event-api'
import type { SalesOrder } from '@/lib/api/sales-order-api'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { MatchedByBadge } from '@/components/accounting/matched-by-badge'
import {
    captureReconciliationFiltersChanged,
    useAssignSearchTracking,
} from '@/components/accounting/reconciliation-tracking'
import { PageBackButton } from '@/components/common/PageBackButton'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { ListCard } from '@/components/common/ListCard'
import { defineListFilters } from '@/components/common/filters/core'
import { dateFilter } from '@/components/common/filters/date-filter'
import { selectFilter } from '@/components/common/filters/select-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    useAssignBalanceEvent,
    useBalanceEvents,
    useIgnoreBalanceEvent,
} from '@/hooks/use-balance-event-query'
import { OrderSearchSelect } from '@/components/forms/order-search-select'
import { formatDateTime, formatVnd, ymdVN } from '@/lib/format'
import { orderRemaining } from '@/lib/order-money'
import { m } from '@/lib/paraglide/messages'

/**
 * Every assign from this screen is a human match with no machine suggestion
 * behind it. Stated once here so the telemetry stays honest when a suggestion
 * flow is added later.
 */
const ASSIGN_MATCH_PROPERTIES = { matched_by: 'manual', suggested: false }

function statusLabel(s: ReconcileStatus): string {
    if (s === 'matched') return m.reconcile_status_matched()
    if (s === 'ignored') return m.reconcile_status_ignored()
    return m.reconcile_status_unmatched()
}

/**
 * Bộ lọc khai một lần, suy ra: parser cho URL, mặc định, badge đếm, chip.
 *
 * Mặc định KHÔNG rỗng — màn này mở ra là xem khoản chưa đối soát — nên
 * `status=unmatched` không được ghi vào URL và cũng không tính là đang lọc.
 */
const FILTERS = defineListFilters({
    status: selectFilter<ReconcileStatus | 'all'>({
        label: m.reconcile_filter_status,
        defaultValue: 'unmatched',
        options: () => [
            { value: 'unmatched', label: m.reconcile_status_unmatched() },
            { value: 'matched', label: m.reconcile_status_matched() },
            { value: 'ignored', label: m.reconcile_status_ignored() },
            { value: 'all', label: m.reconcile_filter_all() },
        ],
    }),
    date_from: dateFilter({ label: m.reconcile_filter_from }),
    date_to: dateFilter({ label: m.reconcile_filter_to }),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/accounting/reconciliation',
)({
    component: ReconciliationPage,
    validateSearch: FILTERS.validateSearch,
})

function ReconciliationPage() {
    const navigate = useNavigate({ from: Route.fullPath })
    const [assigning, setAssigning] = useState<BalanceEvent | null>(null)
    const [ignoring, setIgnoring] = useState<BalanceEvent | null>(null)

    const search = Route.useSearch()
    const { values, barProps } = useListFilters({
        def: FILTERS,
        search,
        onSearchChange: (next, opts) => {
            captureReconciliationFiltersChanged(
                FILTERS.readValues(search),
                FILTERS.readValues(next),
            )
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            })
        },
        columns: 3,
    })

    // Map bộ lọc → tham số API. Cố ý để tường minh ở màn: mỗi màn map một kiểu
    // nên tầng chung không ôm phần này.
    const { data: listRes } = useBalanceEvents({
        type: 'in',
        status: values.status,
        date_from: values.date_from || undefined,
        date_to: values.date_to || undefined,
        per_page: 50,
    })
    const rows = listRes?.data ?? []

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {barProps.activeCount > 0
                    ? m.list_empty_filtered()
                    : m.reconcile_list_empty()}
            </span>
            {barProps.activeCount > 0 ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )

    return (
        <div className="space-y-4 page-pad">
            <div className="flex min-w-0 items-start gap-2">
                <PageBackButton className="-my-2 md:-my-1" />
                <div>
                    <h1 className="text-xl font-semibold">
                        {m.reconcile_title()}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {m.reconcile_subtitle()}
                    </p>
                </div>
            </div>

            <ListFilterBar {...barProps} />

            {/* Mobile: card list */}
            <div className="space-y-2 md:hidden">
                {rows.length === 0
                    ? emptyState
                    : rows.map((row) => (
                          <ListCard
                              key={row.id}
                              title={
                                  <span className="font-mono text-sm font-semibold">
                                      {row.account_number}
                                  </span>
                              }
                              subtitle={ymdVN(row.transacted_at)}
                              metric={{
                                  label: m.reconcile_col_amount(),
                                  value: formatVnd(row.amount),
                              }}
                              badge={
                                  row.reconcile_status ===
                                  'unmatched' ? null : (
                                      <span className="flex items-center gap-1">
                                          <Badge variant="secondary">
                                              {statusLabel(
                                                  row.reconcile_status,
                                              )}
                                          </Badge>
                                          <MatchedByBadge
                                              matchedBy={row.matched_by}
                                          />
                                      </span>
                                  )
                              }
                              actions={
                                  row.reconcile_status === 'unmatched' ? (
                                      <>
                                          <Button
                                              size="sm"
                                              onClick={() => setAssigning(row)}
                                          >
                                              {m.reconcile_assign()}
                                          </Button>
                                          <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => setIgnoring(row)}
                                          >
                                              {m.reconcile_ignore()}
                                          </Button>
                                      </>
                                  ) : null
                              }
                              fields={[
                                  {
                                      label: m.reconcile_col_content(),
                                      value: row.content,
                                      full: true,
                                  },
                              ]}
                          />
                      ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.reconcile_col_date()}</TableHead>
                            <TableHead className="text-right">
                                {m.reconcile_col_amount()}
                            </TableHead>
                            <TableHead>{m.reconcile_col_content()}</TableHead>
                            <TableHead>{m.reconcile_col_account()}</TableHead>
                            <TableHead className="text-right">
                                {m.reconcile_col_actions()}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5}>{emptyState}</TableCell>
                            </TableRow>
                        )}
                        {rows.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell>
                                    {ymdVN(row.transacted_at)}
                                </TableCell>
                                <TableCell className="text-right font-mono">
                                    {formatVnd(row.amount)}
                                </TableCell>
                                <TableCell className="max-w-[20rem] truncate">
                                    {row.content}
                                </TableCell>
                                <TableCell>{row.account_number}</TableCell>
                                <TableCell className="text-right space-x-1">
                                    {row.reconcile_status === 'unmatched' ? (
                                        <>
                                            <Button
                                                size="sm"
                                                onClick={() =>
                                                    setAssigning(row)
                                                }
                                            >
                                                {m.reconcile_assign()}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setIgnoring(row)}
                                            >
                                                {m.reconcile_ignore()}
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Badge variant="secondary">
                                                {statusLabel(
                                                    row.reconcile_status,
                                                )}
                                            </Badge>
                                            <MatchedByBadge
                                                matchedBy={row.matched_by}
                                            />
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {assigning && (
                <AssignDialog
                    event={assigning}
                    onClose={() => setAssigning(null)}
                />
            )}
            {ignoring && (
                <IgnoreDialog
                    event={ignoring}
                    onClose={() => setIgnoring(null)}
                />
            )}
        </div>
    )
}

function AssignDialog({
    event,
    onClose,
}: {
    event: BalanceEvent
    onClose: () => void
}) {
    const workflow = useWorkflowTracking('balance_assign', {
        attemptKey: event.id,
        operation: 'assign',
    })
    const searchTracking = useAssignSearchTracking(workflow)
    const [order, setOrder] = useState<SalesOrder | null>(null)
    // Đã bấm gán một lần nhưng vượt công nợ → hiện cảnh báo, chờ xác nhận lần 2.
    const [warnOverpay, setWarnOverpay] = useState(false)
    const assignMut = useAssignBalanceEvent()

    // Công nợ còn lại của đơn đang chọn (đã trừ hàng trả, cùng số server dùng
    // để chặn OVERPAYMENT); khoản thu vượt mức này = trả dư.
    const remaining = order ? orderRemaining(order) : 0
    const exceeds = !!order && event.amount > remaining + 0.001

    const handleConfirm = async () => {
        if (!order) {
            workflow.step('validation_failed')
            return
        }
        // Vượt công nợ mà chưa xác nhận → chỉ hiện cảnh báo, KHÔNG gán.
        if (exceeds && !warnOverpay) {
            workflow.step('warning_shown')
            setWarnOverpay(true)
            return
        }
        if (exceeds) workflow.step('warning_confirmed')
        await workflow.run(
            () =>
                assignMut.mutateAsync({
                    id: event.id,
                    orderId: order.id,
                    // Chỉ bật override khi thực sự vượt (đơn hợp lệ giữ nguyên luồng cũ).
                    allowOverpayment: exceeds,
                }),
            undefined,
            // Provenance of this match, mirrored from what the API stores, so the
            // telemetry and the ledger tell the same story.
            ASSIGN_MATCH_PROPERTIES,
        )
        onClose()
    }

    const close = () => {
        workflow.finish('closed')
        onClose()
    }
    return (
        <Dialog open onOpenChange={(o) => !o && close()}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>{m.reconcile_assign_title()}</DialogTitle>
                </DialogHeader>
                <div className="min-w-0 space-y-4">
                    {/* Card khoản CK — nổi bật số tiền tiền-về (emerald) + icon chip */}
                    <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900 dark:bg-emerald-950/40">
                        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
                            <ArrowDownLeft className="size-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-xs font-medium uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                                {m.reconcile_assign_incoming()}
                            </div>
                            <div className="font-mono text-lg font-bold text-emerald-700 tabular-nums dark:text-emerald-300">
                                {formatVnd(event.amount)}
                            </div>
                            <div className="mt-1 line-clamp-2 text-sm text-muted-foreground [overflow-wrap:anywhere]">
                                {event.content}
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                                <span className="inline-flex items-center gap-1">
                                    <Landmark className="size-3" />
                                    {event.account_number}
                                </span>
                                <span className="inline-flex items-center gap-1">
                                    <Clock className="size-3" />
                                    {formatDateTime(event.transacted_at)}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div>
                        <Label className="text-sm font-medium">
                            {m.reconcile_assign_pick_order()}
                        </Label>
                        <div className="mt-1.5">
                            <OrderSearchSelect
                                value={order}
                                onSearchResults={searchTracking.onSearchResults}
                                onPick={searchTracking.onPick}
                                onChange={(o) => {
                                    workflow.interact('selection')
                                    setOrder(o)
                                    setWarnOverpay(false)
                                }}
                            />
                        </div>
                    </div>

                    {/* Cảnh báo khoản thu vượt công nợ còn lại — chỉ hiện sau khi
                        nhân viên bấm gán lần đầu. Bấm "Vẫn gán" để override. */}
                    {warnOverpay && exceeds && order && (
                        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                            <AlertTriangle className="mt-0.5 size-5 shrink-0 text-amber-600 dark:text-amber-400" />
                            <div className="min-w-0 text-sm">
                                {m.reconcile_assign_overpay_warn({
                                    amount: formatVnd(event.amount),
                                    remaining: formatVnd(remaining),
                                    code: order.order_code,
                                })}
                            </div>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        disabled={assignMut.isPending}
                        onClick={close}
                    >
                        {m.common_cancel()}
                    </Button>
                    <Button
                        variant={
                            warnOverpay && exceeds ? 'destructive' : 'default'
                        }
                        disabled={!order || assignMut.isPending}
                        onClick={handleConfirm}
                    >
                        {assignMut.isPending && (
                            <Loader2 className="size-4 animate-spin" />
                        )}
                        {warnOverpay && exceeds
                            ? m.reconcile_assign_overpay_confirm()
                            : m.reconcile_assign_confirm()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

function IgnoreDialog({
    event,
    onClose,
}: {
    event: BalanceEvent
    onClose: () => void
}) {
    const [note, setNote] = useState('')
    const ignoreMut = useIgnoreBalanceEvent()
    const workflow = useWorkflowTracking('balance_ignore', {
        attemptKey: event.id,
        operation: 'ignore',
    })
    const close = () => {
        workflow.finish('closed')
        onClose()
    }

    return (
        <Dialog open onOpenChange={(o) => !o && close()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{m.reconcile_ignore_title()}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="rounded border p-2 text-sm">
                        <div className="font-mono font-semibold">
                            {formatVnd(event.amount)}
                        </div>
                        <div className="text-muted-foreground truncate">
                            {event.content}
                        </div>
                    </div>
                    <div>
                        <Label>{m.reconcile_ignore_note()}</Label>
                        <Input
                            value={note}
                            placeholder={m.reconcile_ignore_note_placeholder()}
                            onChange={(e) => {
                                workflow.interact('details')
                                setNote(e.target.value)
                            }}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        disabled={ignoreMut.isPending}
                        onClick={async () => {
                            await workflow.run(() =>
                                ignoreMut.mutateAsync({
                                    id: event.id,
                                    note: note || undefined,
                                }),
                            )
                            onClose()
                        }}
                    >
                        {m.reconcile_ignore_confirm()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
